/**
 * Payments Hook using React Query for Optimized Caching
 *
 * This hook provides payment data fetching with React Query's built-in
 * caching, invalidation, and stale-while-revalidate patterns.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import apiClient from './api-client';

// Types for the payments API
export interface PaymentFilters {
  paymentSourceType: string;
  isNonBillable: boolean;
  page?: number;
  pageSize?: number;
  pageTimestamp?: Date;
  search?: string;
  paymentStatus?: string[];
  [key: string]: unknown; // for additional filter properties
}

export interface PaymentListItem {
  // Add specific payment item properties based on your API response
  id: string;
  checkAllIndicator?: boolean;
  indeterminateCheck?: boolean;
  [key: string]: unknown;
}

export interface PaymentResponse {
  data: PaymentListItem[];
  totalCount?: number;
  hasMore?: boolean;
}

export interface OverviewPayments {
  // Add overview payment properties based on your API response
  [key: string]: unknown;
}

// Query Keys Factory for consistent cache management
export const paymentsQueryKeys = {
  all: ['payments'] as const,
  lists: () => [...paymentsQueryKeys.all, 'list'] as const,
  list: (filters: PaymentFilters) => [...paymentsQueryKeys.lists(), filters] as const,
  overview: (paymentSourceType: string, isNonBillable: boolean) =>
    [...paymentsQueryKeys.all, 'overview', { paymentSourceType, isNonBillable }] as const,
};

// API Functions
const fetchPayments = async (filters: PaymentFilters): Promise<PaymentListItem[]> => {
  const filterPayload = { ...filters };
  filterPayload.paymentStatus = ['SEND_TO_FINANCE'];

  const response = await apiClient.post('/api/portal/billing/review', filterPayload);

  if (response.status === 200) {
    return response.data as PaymentListItem[];
  }

  throw new Error(`Failed to fetch payments: ${response.status}`);
};

const fetchOverviewPayments = async (paymentSourceType: string, isNonBillable: boolean): Promise<OverviewPayments> => {
  // This would call the overview endpoint - adjust based on your actual API
  const response = await apiClient.post('/api/portal/billing/overview', {
    paymentSourceType,
    isNonBillable,
  });

  if (response.status === 200) {
    return response.data as OverviewPayments;
  }

  throw new Error(`Failed to fetch overview payments: ${response.status}`);
};

// Custom Hook for Payments
export interface UsePaymentsOptions {
  enabled?: boolean;
  staleTime?: number;
  cacheTime?: number;
  refetchOnWindowFocus?: boolean;
}

export interface UsePaymentsResult {
  // Data
  payments: PaymentListItem[];
  overviewPayments: OverviewPayments | undefined;

  // Loading states
  isLoading: boolean;
  isLoadingOverview: boolean;
  isFetching: boolean;
  isSearching: boolean;

  // Functions
  refetch: () => void;
  refreshListings: () => void;
  fetchMorePayments: () => void;
  applySearchFilter: (searchTerm: string) => void;
  clearCache: () => void;
  invalidateByFilter: (filterPattern: Partial<PaymentFilters>) => void;

  // Pagination
  hasNextPage: boolean;
  currentPage: number;
}

export const usePayments = (filters: PaymentFilters, options: UsePaymentsOptions = {}): UsePaymentsResult => {
  const queryClient = useQueryClient();

  const {
    enabled = true,
    staleTime = 5 * 60 * 1000, // 5 minutes - similar to original cache TTL
    cacheTime = 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus = false,
  } = options;

  // Main payments query
  const paymentsQuery = useQuery({
    queryKey: paymentsQueryKeys.list(filters),
    queryFn: () => fetchPayments(filters),
    enabled: enabled && Boolean(filters.paymentSourceType) && (!filters.search || filters.search.length >= 2),
    staleTime,
    cacheTime,
    refetchOnWindowFocus,
  });

  // Overview payments query
  const overviewQuery = useQuery({
    queryKey: paymentsQueryKeys.overview(filters.paymentSourceType, filters.isNonBillable),
    queryFn: () => fetchOverviewPayments(filters.paymentSourceType, filters.isNonBillable),
    enabled: enabled && Boolean(filters.paymentSourceType),
    staleTime,
    cacheTime,
    refetchOnWindowFocus,
  });

  // Pagination logic - accumulate results for infinite scroll-like behavior
  const accumulatedPayments = useMemo(() => {
    if (!filters.page || filters.page === 1) {
      return paymentsQuery.data || [];
    }

    // For subsequent pages, we'd need to implement infinite query logic
    // or handle pagination differently based on your requirements
    return paymentsQuery.data || [];
  }, [paymentsQuery.data, filters.page]);

  // Functions
  const refetch = useCallback(() => {
    paymentsQuery.refetch();
    overviewQuery.refetch();
  }, [paymentsQuery, overviewQuery]);

  const refreshListings = useCallback(() => {
    // Invalidate all payment queries to force fresh data
    queryClient.invalidateQueries({ queryKey: paymentsQueryKeys.all });
  }, [queryClient]);

  const fetchMorePayments = useCallback(() => {
    // This would typically be handled by useInfiniteQuery for better pagination
    // For now, we'll update the filters to include the next page
    const nextPageFilters = { ...filters, page: (filters.page || 1) + 1 };
    queryClient.prefetchQuery({
      queryKey: paymentsQueryKeys.list(nextPageFilters),
      queryFn: () => fetchPayments(nextPageFilters),
      staleTime,
    });
  }, [filters, queryClient, staleTime]);

  const applySearchFilter = useCallback((searchTerm: string) => {
    if (!searchTerm || searchTerm.length < 2) {
      return;
    }
  }, []);

  const clearCache = useCallback(() => {
    queryClient.removeQueries({ queryKey: paymentsQueryKeys.all });
  }, [queryClient]);

  const invalidateByFilter = useCallback(
    (filterPattern: Partial<PaymentFilters>) => {
      // Invalidate queries that match the filter pattern
      queryClient.invalidateQueries({
        queryKey: paymentsQueryKeys.lists(),
        predicate: (query) => {
          const queryFilters = query.queryKey[2] as PaymentFilters;
          return Object.keys(filterPattern).every((key) => queryFilters[key] === filterPattern[key]);
        },
      });
    },
    [queryClient],
  );

  return {
    // Data
    payments: accumulatedPayments,
    overviewPayments: overviewQuery.data,

    // Loading states
    isLoading: paymentsQuery.isLoading,
    isLoadingOverview: overviewQuery.isLoading,
    isFetching: paymentsQuery.isFetching || overviewQuery.isFetching,
    isSearching: paymentsQuery.isFetching && Boolean(filters.search),

    // Functions
    refetch,
    refreshListings,
    fetchMorePayments,
    applySearchFilter,
    clearCache,
    invalidateByFilter,

    // Pagination
    hasNextPage: true, // This would be determined by your API response
    currentPage: filters.page || 1,
  };
};

export const usePaymentSearch = (filters: PaymentFilters, options?: UsePaymentsOptions) => {
  return usePayments(filters, {
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
    ...options,
  });
};

export const useSearchCache = () => {
  const queryClient = useQueryClient();

  const getCachedResult = useCallback(
    (searchTerm: string, filters: unknown) => {
      if (!searchTerm || searchTerm.length < 2) return null;

      const queryKey = paymentsQueryKeys.list(filters as PaymentFilters);
      const cachedData = queryClient.getQueryData(queryKey);
      return cachedData || null;
    },
    [queryClient],
  );

  const setCachedResult = useCallback(
    (searchTerm: string, filters: unknown, result: unknown) => {
      if (!searchTerm || searchTerm.length < 2) return;

      const queryKey = paymentsQueryKeys.list(filters as PaymentFilters);
      queryClient.setQueryData(queryKey, result);
    },
    [queryClient],
  );

  const clearCache = useCallback(() => {
    queryClient.removeQueries({ queryKey: paymentsQueryKeys.all });
  }, [queryClient]);

  const clearByFilter = useCallback(
    (filterPattern: unknown) => {
      queryClient.invalidateQueries({
        queryKey: paymentsQueryKeys.lists(),
        predicate: (query) => {
          const queryFilters = query.queryKey[2] as PaymentFilters;
          const pattern = filterPattern as Partial<PaymentFilters>;
          return Object.keys(pattern).some((key) => JSON.stringify(queryFilters[key]) === JSON.stringify(pattern[key]));
        },
      });
    },
    [queryClient],
  );

  const getStats = useCallback(
    () => ({
      size: queryClient.getQueryCache().getAll().length,
      maxEntries: 100, // React Query manages this internally
      ttlMs: 5 * 60 * 1000,
    }),
    [queryClient],
  );

  return {
    getCachedResult,
    setCachedResult,
    clearCache,
    clearByFilter,
    getStats,
  };
};
